import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import useMetrics from "./useMetrics.js";
import useGlobalContext from "./useGlobalContext.js";
import { flightsLocalStore } from "../utils/local-store.js";
import { getFlight } from "../store/flightDetails/selectors.js";
import useFlightDetailsFromSearchResults from "./useFlightDetailsFromSearchResults.js";
import { GRAPHITE_METRIC_AGGREGATION_NAMESPACE_FUNNEL as nameSpace } from "@flights/constants";
import useEvents from "./useEvents.js";
export default function useReportSRFDMismatch(id) {
    const nodeName = "srfd_mismatch";
    const { trackMetrics } = useMetrics();
    const { features } = useGlobalContext();
    const flightFD = useSelector(getFlight);
    const flightSR = useFlightDetailsFromSearchResults(id || "");
    const { grumble } = useEvents();
    const isReported = useCallback((id) => {
        return !!flightsLocalStore?.get(`${id}_srfd_reported`);
    }, []);
    const setReported = useCallback((id) => {
        flightsLocalStore?.set(`${id}_srfd_reported`, true);
    }, []);
    const report = useCallback((id, diff) => {
        const state = diff.length > 0 ? "mismatch" : "match";
        trackMetrics(nameSpace, nodeName, [["by_mismatch_state", state]], "count");
        if (state === "mismatch")
            grumble("SR-FD : flight mismatches", { id, diff });
        setReported(id);
    }, [trackMetrics, grumble, setReported]);
    useEffect(() => {
        if (!features.FLIGHTS_WEB_REPORT_SRFD_MISMATCH_METRICS)
            return;
        if (!id)
            return;
        if (!flightFD)
            return;
        if (!flightSR)
            return;
        if (isReported(id))
            return;
        try {
            const diff = getDiff(flightSR, flightFD);
            report(id, diff);
        }
        catch (e) { }
    }, [features, id, flightFD, flightSR, isReported, report]);
}
function carriersSignature(carriers) {
    return [...new Set(carriers ?? [])].sort().join(",");
}
function getDiff(flightSR, flightFD) {
    if (!flightSR || !flightFD)
        return [];
    let flightNumber = false, legsCount = false, totalTime = false, departureAirport = false, arrivalAirport = false, carriers = false;
    const fdSegments = flightFD.segments ?? [];
    const srSegments = flightSR.segments ?? [];
    const segmentCount = Math.max(fdSegments.length, srSegments.length);
    for (let sidx = 0; sidx < segmentCount; sidx++) {
        const segmentFD = fdSegments[sidx];
        const segmentSR = srSegments[sidx];
        if (segmentFD?.totalTime !== segmentSR?.totalTime) {
            totalTime = true;
        }
        if ((segmentFD?.legs?.length ?? 0) !== (segmentSR?.legs?.length ?? 0)) {
            legsCount = true;
        }
        if (segmentFD?.departureAirport?.code !== segmentSR?.departureAirport?.code) {
            departureAirport = true;
        }
        if (segmentFD?.arrivalAirport?.code !== segmentSR?.arrivalAirport?.code) {
            arrivalAirport = true;
        }
        const legsFD = segmentFD?.legs ?? [];
        const legsSR = segmentSR?.legs ?? [];
        const legCount = Math.max(legsFD.length, legsSR.length);
        for (let lidx = 0; lidx < legCount; lidx++) {
            const legFD = legsFD[lidx];
            const legSR = legsSR[lidx];
            if (legFD?.flightInfo?.flightNumber !== legSR?.flightInfo?.flightNumber) {
                flightNumber = true;
            }
            if (carriersSignature(legFD?.carriers) !== carriersSignature(legSR?.carriers)) {
                carriers = true;
            }
        }
    }
    const data = { flightNumber, legsCount, totalTime, departureAirport, arrivalAirport, carriers };
    return Object.keys(data).filter((key) => data[key]);
}
